import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, NumberField, SaveBar, MissingConfigBanner } from '../components/Form';
import { useConfigDoc, useWriteConfig } from '../lib/configHooks';

// Places radius is stored in meters but shown in miles.
const M_PER_MI = 1609.34;
const metersToMiles = (m?: number) =>
  typeof m === 'number' ? Math.round((m / M_PER_MI) * 10) / 10 : undefined;
const milesToMeters = (mi: number) => Math.round(mi * M_PER_MI);

interface ServiceArea {
  [key: string]: unknown;
  maxAssignDistanceMiles?: number;
  placesAutocompleteRadiusMeters?: number;
  excludedZipPrefixes?: string[];
  includedZipPrefixes?: string[];
}

export default function ServiceAreaPage() {
  const { data } = useConfigDoc<ServiceArea>('service_area');
  const write = useWriteConfig('service_area');
  const [draft, setDraft] = useState<ServiceArea>({});
  const [dirty, setDirty] = useState(false);
  const [zipText, setZipText] = useState('');
  const [includeZipText, setIncludeZipText] = useState('');

  useEffect(() => {
    if (!dirty) {
      setDraft(data || {});
      setZipText((data?.excludedZipPrefixes || []).join(', '));
      setIncludeZipText((data?.includedZipPrefixes || []).join(', '));
    }
  }, [data, dirty]);

  const parsePrefixes = (text: string) =>
    text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const onSave = async () => {
    await write.mutateAsync({
      ...draft,
      excludedZipPrefixes: parsePrefixes(zipText),
      includedZipPrefixes: parsePrefixes(includeZipText),
    });
    setDirty(false);
  };

  return (
    <>
      <PageHeader
        title="Service Area"
        subtitle="Driver assignment radius and address autocomplete bias."
      />
      {data && Object.keys(data).length === 0 && (
        <MissingConfigBanner docIds={['service_area']} />
      )}
      <Card title="Coverage">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="Max assign distance"
            value={draft.maxAssignDistanceMiles}
            onChange={(v) => {
              setDraft({ ...draft, maxAssignDistanceMiles: v });
              setDirty(true);
            }}
            min={1}
            max={500}
            step={1}
            suffix="mi"
            hint="Drivers beyond this radius from pickup will not be offered the job."
          />
          <NumberField
            label="Places autocomplete radius"
            value={metersToMiles(draft.placesAutocompleteRadiusMeters)}
            onChange={(v) => {
              setDraft({
                ...draft,
                placesAutocompleteRadiusMeters: milesToMeters(v),
              });
              setDirty(true);
            }}
            min={0.1}
            max={30}
            step={0.1}
            suffix="mi"
            hint="Bias for the customer address autocomplete (0.1–30 mi)."
          />
        </div>
      </Card>
      <Card title="Included ZIP prefixes (allowlist)">
        <label className="block text-sm">
          <div className="mb-1 font-medium text-slate-700">
            Comma-separated prefixes
          </div>
          <input
            type="text"
            value={includeZipText}
            onChange={(e) => {
              setIncludeZipText(e.target.value);
              setDirty(true);
            }}
            placeholder="e.g. 900, 902"
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-slate-500">
            When set, ONLY destinations whose ZIP starts with one of these
            prefixes are serviceable — all others are blocked at checkout. Leave
            empty to allow everywhere (except the excluded prefixes below).
          </p>
        </label>
      </Card>
      <Card title="Excluded ZIP prefixes">
        <label className="block text-sm">
          <div className="mb-1 font-medium text-slate-700">
            Comma-separated prefixes
          </div>
          <input
            type="text"
            value={zipText}
            onChange={(e) => {
              setZipText(e.target.value);
              setDirty(true);
            }}
            placeholder="e.g. 902, 903"
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-slate-500">
            Orders with destination ZIPs matching any of these prefixes will be
            blocked at checkout.
          </p>
        </label>
      </Card>
      <SaveBar
        dirty={dirty}
        saving={write.isPending}
        onSave={onSave}
        onReset={() => {
          setDraft(data || {});
          setZipText((data?.excludedZipPrefixes || []).join(', '));
          setIncludeZipText((data?.includedZipPrefixes || []).join(', '));
          setDirty(false);
        }}
      />
    </>
  );
}
